/**
 * Flag NAMES, apart from the query hooks that read them (FRONT-1986).
 *
 * The feature-flags query-hook module is a TanStack Query module —
 * importing it just to name a flag string drags the data layer into
 * whatever imports it. A plain, dependency-light lib module
 * (`settings-sections.ts`) needs to name a flag without that cost, so flag
 * constants that a lib module needs live here instead, with no imports of
 * their own. The query-hook module re-exports this constant so every
 * existing importer keeps working unchanged.
 */

/**
 * Flag name for Eddy Machine (FRONT-1985) — mirrors the backend flag
 * registry. Unlike every other flag in the feature-flags query-hook module,
 * this one is constrained per PERSON (`userId`) on the server, not per org
 * — see `src/core/eddy-machine-flag.ts`.
 *
 * Read with fallback `false`: this is a dark release and must be absent
 * until the flag exists in Unleash and is on for this specific person. The
 * server 404s every Eddy Machine route on the same flag, so a briefly-wrong
 * client-side `true` could never reach real data anyway — but the control
 * itself (register/run/see a machine) must not appear before the flag says
 * so, the same "off means gone, not greyed out" posture as
 * `TENANT_EXTRA_MODELS_FLAG` in that module.
 */
export const EDDY_MACHINE_FLAG = 'eddy.machine';

/**
 * Flag name for audio answers (FRONT-2387) — mirrors the backend flag
 * registry. Read with fallback `false`: this is a dark release, the same
 * "off means gone, not greyed out" posture as `EDDY_MACHINE_FLAG` above — no
 * play control, no audio until the toggle exists in Unleash and is on.
 */
export const EDDY_AUDIO_ANSWERS_FLAG = 'eddy.audio-answers';

/**
 * Flag name for the workspace brand kit (FRONT-1822) — mirrors the backend flag
 * registry. Read with fallback `false`: the System Settings → Brand section is
 * absent until the flag is on, and every brand-kit route 404s while it is off.
 */
export const BRAND_KIT_FLAG = 'eddy.brand-kit';

/**
 * Flag name for Eddy Pro Builder (FRONT-3003) — mirrors the backend flag
 * registry. Per-PERSON flag (constrained on `userId` server-side), read with
 * fallback `false`: a dark release, "off means gone, not greyed out" — no
 * sidebar entry, no Settings section, no admin card until it is on.
 */
export const PRO_BUILDER_FLAG = 'eddy.pro-builder';
